import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_CLS = {
  trialing: "bg-sky-900/40 text-sky-300 border-sky-800",
  active: "bg-emerald-900/40 text-emerald-300 border-emerald-800",
  expired: "bg-red-900/40 text-red-300 border-red-800",
  cancelled: "bg-zinc-800 text-zinc-400 border-zinc-700",
};

export default function SuperSubscriptions() {
  const [items, setItems] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ plan_id: "", extend_days: 0, subscription_status: "" });

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/super/subscriptions"), api.get("/super/plans")]);
    setItems(s.data);
    setPlans(p.data.filter((x) => x.status === "active"));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {};
      if (form.plan_id) payload.plan_id = form.plan_id;
      if (form.subscription_status) payload.subscription_status = form.subscription_status;
      if (Number(form.extend_days)) payload.extend_days = Number(form.extend_days);
      await api.post(`/super/subscriptions/${selected.company_id}`, payload);
      toast.success("Updated");
      setSelected(null);
      setForm({ plan_id: "", extend_days: 0, subscription_status: "" });
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="super-subscriptions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Customers</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-white">Subscriptions</h1>
      </div>

      <div className="bg-[#18181B] border border-white/5 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5"><TableHead className="text-zinc-400">Company</TableHead><TableHead className="text-zinc-400">Status</TableHead><TableHead className="text-zinc-400">Plan</TableHead><TableHead className="text-zinc-400">Trial ends</TableHead><TableHead className="text-zinc-400">Renews</TableHead><TableHead className="text-zinc-400 text-right">Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.company_id} className="border-white/5 hover:bg-white/5" data-testid={`super-sub-row-${s.company_id}`}>
                <TableCell className="text-zinc-200">{s.company_name}<div className="text-xs text-zinc-500">{s.email}</div></TableCell>
                <TableCell><Badge variant="outline" className={STATUS_CLS[s.subscription_status] || "bg-zinc-800 text-zinc-400 border-zinc-700"}>{s.subscription_status || "—"}</Badge></TableCell>
                <TableCell className="text-zinc-300">{s.plan_name || "—"}</TableCell>
                <TableCell className="text-zinc-400 text-xs">{s.trial_ends_at ? new Date(s.trial_ends_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-zinc-400 text-xs">{s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="text-zinc-300 hover:bg-white/10" onClick={() => { setSelected(s); setForm({ plan_id: "", extend_days: 0, subscription_status: "" }); }} data-testid={`super-sub-edit-${s.company_id}`}>Manage</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-[#18181B] border-white/10 text-zinc-200">
          <DialogHeader><DialogTitle>{selected?.company_name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Assign plan (activates immediately)</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger className="bg-[#0a0a0c] border-white/10 mt-1" data-testid="super-sub-plan-select"><SelectValue placeholder="No change" /></SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · ₹{p.price}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Or set status manually</Label>
                <Select value={form.subscription_status} onValueChange={(v) => setForm({ ...form, subscription_status: v })}>
                  <SelectTrigger className="bg-[#0a0a0c] border-white/10 mt-1"><SelectValue placeholder="No change" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trialing">Trialing</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Extend by days</Label>
                <Input className="bg-[#0a0a0c] border-white/10 mt-1" type="number" value={form.extend_days} onChange={(e) => setForm({ ...form, extend_days: e.target.value })} data-testid="super-sub-extend-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Close</Button>
            <Button onClick={save} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white" data-testid="super-sub-save-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
